// API の呼び出し。ID トークンと文脈のヘッダを付け、失敗は日本語の文を持つ ApiError にする。
// トークンを付けて 401 が返ったら、セッションを消してログインへ戻す（旧実装はここで黙っていた）。
import { clearSession, getIdToken, loginPathFor } from './auth';
import { getConfig } from './config';
import { apiErrorMessage } from './errors';

export const OFFICE_HEADER = 'X-Alfine-Office';
export const SCOPE_HEADER = 'X-Alfine-Scope';

export class ApiError extends Error {
  readonly userMessage: string;
  constructor(
    readonly status: number,
    readonly code: string,
    readonly serverMessage: string,
    readonly details?: unknown,
  ) {
    super(apiErrorMessage(status, code, serverMessage));
    this.userMessage = this.message;
  }
}

/** 失敗の本文 { error: { code, message, details } } を読む */
export function parseErrorBody(json: unknown): { code: string; message: string; details?: unknown } {
  const e = (json as { error?: { code?: unknown; message?: unknown; details?: unknown } } | null)?.error;
  return { code: typeof e?.code === 'string' ? e.code : 'ERROR', message: typeof e?.message === 'string' ? e.message : '', details: e?.details };
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  officeId?: string | null; // X-Alfine-Office
  ops?: boolean; // X-Alfine-Scope: ops
  signal?: AbortSignal;
}

export function buildHeaders(token: string, o: Pick<RequestOptions, 'officeId' | 'ops' | 'body'>): Record<string, string> {
  const h: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (o.body !== undefined) h['Content-Type'] = 'application/json';
  if (o.ops) h[SCOPE_HEADER] = 'ops';
  else if (o.officeId) h[OFFICE_HEADER] = o.officeId;
  return h;
}

function toLogin(): void {
  clearSession();
  const here = window.location.pathname + window.location.search;
  window.location.replace(loginPathFor(here));
}

export async function api<T>(path: string, o: RequestOptions = {}): Promise<T> {
  const token = await getIdToken();
  if (!token) {
    toLogin();
    throw new ApiError(401, 'UNAUTHORIZED', '');
  }
  let res: Response;
  try {
    res = await fetch(`${getConfig().apiUrl}${path}`, {
      method: o.method ?? (o.body === undefined ? 'GET' : 'POST'),
      headers: buildHeaders(token, o),
      body: o.body === undefined ? undefined : JSON.stringify(o.body),
      signal: o.signal,
    });
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') throw e;
    throw new ApiError(0, 'API_UNREACHABLE', '');
  }
  if (res.status === 401) {
    toLogin();
    throw new ApiError(401, 'UNAUTHORIZED', '');
  }
  if (res.status === 204) return undefined as T;
  const json = await res.json().catch(() => null);
  if (!res.ok) {
    const e = parseErrorBody(json);
    throw new ApiError(res.status, e.code, e.message, e.details);
  }
  return json as T;
}

/** 事業所の経路を組む（ID は URL に入れるので符号化する） */
export function officePath(officeId: string, ...parts: string[]): string {
  return ['/offices', officeId, ...parts].map((p, i) => (i === 0 ? p : encodeURIComponent(p))).join('/');
}
